import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { usePagination } from './usePagination';

import CrmService, { CrmQueryFiltterDto } from '@/services/CrmService';

export const CRMS_QUERY_KEY: unknown[] = ['crms'];

export function useGetCrms(
  crmQueryFilterDto: Omit<CrmQueryFiltterDto, 'page' | 'limit'>,
) {
  const [isFirstSearch, setIsFirstSearch] = useState(true);

  const LIMIT = 10;

  const hasSearchFilter = Object.entries(crmQueryFilterDto).some(
    ([key, value]) => value !== undefined && key !== 'page' && key !== 'limit',
  );

  const {
    currentPage,
    totalPages,
    totalItems,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
    setPage,
    setTotalPages,
    setTotalItems,
  } = usePagination();

  const { data, isFetching } = useQuery({
    queryKey: CRMS_QUERY_KEY.concat([
      {
        ...crmQueryFilterDto,
        page: isFirstSearch && hasSearchFilter ? 1 : currentPage,
        limit: LIMIT,
      },
    ]),
    queryFn: async () => {
      const response = await CrmService.findAll({
        ...crmQueryFilterDto,
        page: currentPage,
        limit: LIMIT,
      });

      return response;
    },
  });

  useEffect(() => {
    if (isFirstSearch && hasSearchFilter) {
      setPage(1);
      setIsFirstSearch(false);
    }

    if (!hasSearchFilter) {
      setIsFirstSearch(true);
    }
  }, [hasSearchFilter, isFirstSearch, setPage]);

  useEffect(() => {
    setTotalPages(data?.totalPages ?? 1);
    setTotalItems(data?.totalItems ?? 0);
  }, [data, setTotalItems, setTotalPages]);

  return {
    crms: data?.items ?? [],
    isLoading: isFetching,
    pagination: {
      currentPage,
      totalPages,
      totalItems,
      hasNextPage,
      hasPreviousPage,
      nextPage,
      previousPage,
      setPage,
    },
  };
}
